import React, { useEffect, useState } from 'react';
import { logError } from '@edx/frontend-platform/logging';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@edx/frontend-platform/i18n';
import { snakeCaseObject } from '@edx/frontend-platform/utils';
import {
  ActionRow, Button, FullscreenModal, StatefulButton, useToggle,
} from '@openedx/paragon';
import { sendEnterpriseTrackEvent } from '@edx/frontend-enterprise-utils';

import LmsApiService from '../../data/services/LmsApiService';
import SystemErrorAlertModal
  from '../learner-credit-management/cards/assignment-allocation-status-modals/SystemErrorAlertModal';
import CreateGroupModalContent from './CreateGroupModalContent';
import EVENT_NAMES from '../../eventTracking';
import { learnerCreditManagementQueryKeys } from '../learner-credit-management/data';
import { useValidatedEmailsContext } from './data/ValidatedEmailsContext';
import { checkForInviteErrors, GroupErrorType } from './utils';

export type CreateGroupModalProps = {
  isModalOpen: boolean,
  closeModal: () => void,
  enterpriseUUID: string,
  onInviteError: (errorType: GroupErrorType) => void
};

const CreateGroupModal = ({
  isModalOpen,
  closeModal,
  enterpriseUUID,
  onInviteError,
}: CreateGroupModalProps) => {
  const intl = useIntl();
  const {
    validatedEmails: learnerEmails,
    canInvite: canInviteMembers,
    isCreateGroupFileUpload,
    isCreateGroupListSelection,
  } = useValidatedEmailsContext();
  const [createButtonState, setCreateButtonState] = useState('default');
  const [groupName, setGroupName] = useState('');
  const [canCreateGroup, setCanCreateGroup] = useState(false);
  const [isSystemErrorModalOpen, openSystemErrorModal, closeSystemErrorModal] = useToggle(false);

  const handleCloseCreateGroupModal = () => {
    closeModal();
    setCreateButtonState('default');
  };
  const queryClient = useQueryClient();
  const handleCreateGroup = async () => {
    setCreateButtonState('pending');
    const options = {
      enterpriseUUID,
      groupName,
    };
    let groupCreationResponse;
    try {
      groupCreationResponse = await LmsApiService.createEnterpriseGroup(options);
      sendEnterpriseTrackEvent(
        enterpriseUUID,
        EVENT_NAMES.PEOPLE_MANAGEMENT.CREATE_GROUP_MODAL_BUTTON_SUBMIT,
        { status: 'success' },
      );
      if (isCreateGroupListSelection && isCreateGroupFileUpload) {
        sendEnterpriseTrackEvent(
          enterpriseUUID,
          EVENT_NAMES.PEOPLE_MANAGEMENT.GROUP_CREATE_WITH_CSV_AND_LIST,
        );
      } else if (isCreateGroupListSelection) {
        sendEnterpriseTrackEvent(
          enterpriseUUID,
          EVENT_NAMES.PEOPLE_MANAGEMENT.GROUP_CREATE_WITH_LIST_SELECTION,
        );
      } else if (isCreateGroupFileUpload) {
        sendEnterpriseTrackEvent(
          enterpriseUUID,
          EVENT_NAMES.PEOPLE_MANAGEMENT.GROUP_CREATE_WITH_UPLOAD_CSV,
        );
      }
    } catch (err) {
      logError(err);
      setCreateButtonState('error');
      openSystemErrorModal();
      sendEnterpriseTrackEvent(
        enterpriseUUID,
        EVENT_NAMES.PEOPLE_MANAGEMENT.CREATE_GROUP_MODAL_BUTTON_SUBMIT,
        {
          status: 'error',
          message: err,
        },
      );
    }

    try {
      const requestBody = snakeCaseObject({
        learnerEmails,
      });
      const { data: inviteResponse } = await LmsApiService.inviteEnterpriseLearnersToGroup(
        groupCreationResponse.data.uuid,
        requestBody,
      );
      const { hasErrors, errorType } = checkForInviteErrors(inviteResponse);
      if (hasErrors) {
        onInviteError(errorType);
        sendEnterpriseTrackEvent(
          enterpriseUUID,
          EVENT_NAMES.PEOPLE_MANAGEMENT.ADD_LEARNER_ERROR_NOT_IN_ORG,
        );
      }
      queryClient.invalidateQueries({
        queryKey: learnerCreditManagementQueryKeys.group(enterpriseUUID),
      });
      setCreateButtonState('complete');
      handleCloseCreateGroupModal();
    } catch (err) {
      logError(err);
      setCreateButtonState('error');
      openSystemErrorModal();
    }
  };

  useEffect(() => {
    setCanCreateGroup(false);
    if (groupName.length > 0 && canInviteMembers) {
      setCanCreateGroup(true);
    }
  }, [groupName, canInviteMembers]);

  return (
    <>
      <FullscreenModal
        className="stepper-modal bg-light-200"
        isOpen={isModalOpen}
        onClose={handleCloseCreateGroupModal}
        title={intl.formatMessage({
          id: 'peopleManagement.tab.create.group.modal.title',
          defaultMessage: 'New group',
          description: 'Title for creating a new group modal',
        })}
        footerNode={(
          <ActionRow>
            <ActionRow.Spacer />
            <Button variant="tertiary" onClick={handleCloseCreateGroupModal}>Cancel</Button>
            <StatefulButton
              labels={{
                default: 'Create',
                pending: 'Creating...',
                complete: 'Created',
                error: 'Try again',
              }}
              variant="primary"
              state={createButtonState}
              disabled={!canCreateGroup}
              onClick={handleCreateGroup}
            />
          </ActionRow>
        )}
        isOverflowVisible={false}
      >
        <CreateGroupModalContent
          onSetGroupName={setGroupName}
          isGroupInvite
        />
      </FullscreenModal>
      <SystemErrorAlertModal
        isErrorModalOpen={isSystemErrorModalOpen}
        closeErrorModal={closeSystemErrorModal}
        closeAssignmentModal={handleCloseCreateGroupModal}
        retry={handleCreateGroup}
      />
    </>
  );
};

const mapStateToProps = state => ({
  enterpriseUUID: state.portalConfiguration.enterpriseId,
});

CreateGroupModal.propTypes = {
  enterpriseUUID: PropTypes.string.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(CreateGroupModal);
